import React from 'react';
import { ResultComponentProps } from '../../common/types';
import BaseResultDisplay from '../../common/BaseResultDisplay';
import { Table, Button, message, Tooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

// 截断序列显示函数
const truncateSequence = (sequence: string, maxLength: number = 60) => {
  if (!sequence) return '';
  return sequence.length > maxLength ? sequence.substring(0, maxLength) + '...' : sequence;
};

// 不稳定性指数结果展示组件
// 在单条模式处理中添加对任务管理器数据的支持
const InstabilityIndexResult: React.FC<ResultComponentProps> = ({ data, sequence, title, onDeleteResult, multiResults, onExportAllCSV, ...rest }) => {
  // 批量模式：检查数据结构而不是依赖multiResults参数
  if (data && data.results && Array.isArray(data.results)) {
    const [tableData, setTableData] = React.useState<any[]>([]);
    React.useEffect(() => {
      const rawResults = data.results.length === 1 && Array.isArray(data.results[0]?.result?.results)
        ? data.results[0].result.results
        : data.results;

      const formatted = rawResults.map((item: any, index: number) => ({
        key: String(index + 1),
        id: item.sequence_id || item.id || `序列${index + 1}`,
        sequence: item.sequence || '',
        length: item.result?.sequence_length || item.sequence_length || item.sequence?.length || 0,
        instabilityIndex: item.result?.instability_index || item.instability_index || 0,
        status: (item.result?.is_stable ?? item.is_stable) ? '稳定' : '不稳定',
        success: item.success !== false,
        error: item.error || null
      }));
      setTableData(formatted);
    }, [data]);

    const handleExportCSV = () => {
      try {
        const headers = ['ID', '序列', '长度', '不稳定性指数', '状态'].join(',');
        const rows = tableData.map(item => [
          `"${item.id}"`,
          `"${item.sequence}"`, // 完整序列
          item.length,
          item.instabilityIndex.toFixed(2),
          item.status
        ].join(','));
        const csvContent = `${headers}\n${rows.join('\n')}`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `不稳定性指数结果_${new Date().toISOString().slice(0,10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success('CSV导出成功');
      } catch (error) {
        console.error(error);
        message.error('CSV导出失败');
      }
    };

    const columns = [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 100 },
      { 
        title: '序列', 
        dataIndex: 'sequence', 
        key: 'sequence', 
        width: 200,
        render: (sequence: string) => (
          <Tooltip title={sequence} placement="topLeft">
            <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
              {truncateSequence(sequence)}
            </span>
          </Tooltip>
        )
      },
      { title: '长度', dataIndex: 'length', key: 'length' },
      { title: '不稳定性指数', dataIndex: 'instabilityIndex', key: 'instabilityIndex', render: (value: number) => value.toFixed(2) },
      { title: '状态', dataIndex: 'status', key: 'status' },
    ];

    return (
      <BaseResultDisplay data={{ results: tableData }} title={title || '蛋白质稳定性分析（批量）'} onDeleteResult={onDeleteResult} multiResults showRawData={false} showDistribution={false}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleExportCSV}>导出CSV</Button>
          {onExportAllCSV && <Button icon={<DownloadOutlined />} onClick={onExportAllCSV}>导出所有结果</Button>}
        </div>
        <Table columns={columns} dataSource={tableData} pagination={tableData.length > 10 ? { pageSize: 10 } : false} bordered size="middle" scroll={{ x: 'max-content' }} />
      </BaseResultDisplay>
    );
  }

  // 单条模式：表格展示（删除原来的可视化展示）
  let instabilityIndex = 0;
  let isStable = false;
  let sequenceLength = 0;
  
  // 处理任务管理器返回的数据结构
  if (data && data.results && Array.isArray(data.results) && data.results.length > 0) {
    const sourceResults = data.results.length === 1 && Array.isArray(data.results[0]?.result?.results)
      ? data.results[0].result.results
      : data.results;

    const singleEntry = sourceResults[0];
    if (singleEntry?.success && singleEntry?.result) {
      instabilityIndex = singleEntry.result.instability_index || 0;
      isStable = singleEntry.result.is_stable || false;
      sequenceLength = singleEntry.result.sequence_length || 0;
    }
  } else if (Array.isArray(data) && data.length > 0) {
    const singleEntry = data[0];
    instabilityIndex = singleEntry?.instability_index || singleEntry?.result?.instability_index || 0;
    isStable = singleEntry?.is_stable || singleEntry?.result?.is_stable || false;
    sequenceLength = singleEntry?.sequence_length || singleEntry?.result?.sequence_length || (singleEntry?.sequence?.length ?? 0);
  } else {
    // 原有的直接数据格式
    instabilityIndex = data?.instability_index || 0;
    isStable = data?.is_stable || false;
    sequenceLength = data?.sequence_length || 0;
  }
  
  const singleTableData = [{
    key: '1',
    id: '序列1',
    sequence: sequence || '',
    length: sequenceLength,
    instabilityIndex: instabilityIndex,
    status: isStable ? '稳定' : '不稳定',
  }];

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 100 },
    { 
      title: '序列', 
      dataIndex: 'sequence', 
      key: 'sequence', 
      width: 200,
      render: (sequence: string) => (
        <Tooltip title={sequence} placement="topLeft">
          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {truncateSequence(sequence)}
          </span>
        </Tooltip>
      )
    },
    { title: '长度', dataIndex: 'length', key: 'length' },
    { title: '不稳定性指数', dataIndex: 'instabilityIndex', key: 'instabilityIndex', render: (value: number) => value.toFixed(2) },
    { title: '状态', dataIndex: 'status', key: 'status' },
  ];

  return (
    <BaseResultDisplay 
      data={data} 
      title={title || '蛋白质稳定性分析'} 
      onDeleteResult={onDeleteResult} 
      multiResults={multiResults}
      showRawData={false}
      showDistribution={false}
      {...rest}
    >
      <Table columns={columns} dataSource={singleTableData} pagination={false} bordered size="middle" scroll={{ x: 'max-content' }} />
    </BaseResultDisplay>
  );
};

export default InstabilityIndexResult;